import React, { ChangeEvent, useEffect, useState } from 'react';
import { useToast } from '../contexts/ToastContext';
import { usersService } from '../services';
import { NotificationPreferences, User } from '../types';

interface NotificationPrefsCardProps {
  user: User;
  onSaved: (updated: User) => void;
}

type AssignedPreference = 'normal' | 'off';
type FollowupPreference = 'normal' | 'weekly' | 'off';

/** Displays and saves a user's email notification preferences. */
const NotificationPrefsCard: React.FC<NotificationPrefsCardProps> = ({
  user,
  onSaved,
}) => {
  const { showSuccess, showError } = useToast();
  const [emailEnabled, setEmailEnabled] = useState(user.email_notifications_enabled);
  const [taskAssigned, setTaskAssigned] = useState<AssignedPreference>('normal');
  const [taskFollowups, setTaskFollowups] = useState<FollowupPreference>('normal');
  const [inspectionComplete, setInspectionComplete] =
    useState<AssignedPreference>('normal');
  const [taskActivity, setTaskActivity] = useState<AssignedPreference>('normal');
  const [labAlerts, setLabAlerts] = useState<AssignedPreference>('normal');
  const [wrmp, setWrmp] = useState<AssignedPreference>('normal');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const preferences = user.notification_preferences ?? {};
    setEmailEnabled(user.email_notifications_enabled);
    setTaskAssigned(preferences.task_assigned === 'off' ? 'off' : 'normal');
    setTaskFollowups(
      preferences.task_followups === 'weekly' || preferences.task_followups === 'off'
        ? preferences.task_followups
        : 'normal',
    );
    setInspectionComplete(
      preferences.inspection_complete === 'off' ? 'off' : 'normal',
    );
    setTaskActivity(preferences.task_activity === 'off' ? 'off' : 'normal');
    setLabAlerts(preferences.lab_alerts === 'off' ? 'off' : 'normal');
    setWrmp(preferences.wrmp === 'off' ? 'off' : 'normal');
  }, [user]);

  const storedPreferences = user.notification_preferences ?? {};
  const initialTaskAssigned =
    storedPreferences.task_assigned === 'off' ? 'off' : 'normal';
  const initialTaskFollowups =
    storedPreferences.task_followups === 'weekly' ||
    storedPreferences.task_followups === 'off'
      ? storedPreferences.task_followups
      : 'normal';
  const initialInspectionComplete =
    storedPreferences.inspection_complete === 'off' ? 'off' : 'normal';
  const initialTaskActivity = storedPreferences.task_activity === 'off' ? 'off' : 'normal';
  const initialLabAlerts = storedPreferences.lab_alerts === 'off' ? 'off' : 'normal';
  const initialWrmp = storedPreferences.wrmp === 'off' ? 'off' : 'normal';
  const dirty =
    emailEnabled !== user.email_notifications_enabled ||
    taskAssigned !== initialTaskAssigned ||
    taskFollowups !== initialTaskFollowups ||
    inspectionComplete !== initialInspectionComplete ||
    taskActivity !== initialTaskActivity ||
    labAlerts !== initialLabAlerts ||
    wrmp !== initialWrmp;

  const handleSave = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    const notificationPreferences: NotificationPreferences = {
      task_assigned: taskAssigned,
      task_followups: taskFollowups,
      inspection_complete: inspectionComplete,
      task_activity: taskActivity,
      lab_alerts: labAlerts,
      wrmp,
    };
    try {
      const updated = await usersService.updateUser(user.id, {
        email_notifications_enabled: emailEnabled,
        notification_preferences: notificationPreferences,
      });
      onSaved(updated);
      showSuccess('Preferences updated');
    } catch (err) {
      showError(
        'Could not save preferences',
        err instanceof Error ? err.message : 'Please try again.',
      );
    } finally {
      setSaving(false);
    }
  };

  const selectStyle = { width: '100%' };
  const rowStyle = { display: 'grid', gap: '0.35rem' };
  const helperStyle = {
    color: 'var(--text-secondary)',
    fontSize: '0.875rem',
  };

  return (
    <section
      className="card"
      style={{ padding: '1.5rem', maxWidth: 640, display: 'grid', gap: '1rem' }}
    >
      <h2 style={{ marginTop: 0 }}>Email notification preferences</h2>

      <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
        <input
          type="checkbox"
          aria-label="Email notifications"
          checked={emailEnabled}
          onChange={(event) => setEmailEnabled(event.target.checked)}
          style={{ marginTop: '0.25rem' }}
        />
        <span>
          <strong>Email notifications</strong>
          <br />
          <span style={helperStyle}>
            Master switch. When off, H2Okay never emails you; in-app notifications
            continue.
          </span>
        </span>
      </label>

      <label style={rowStyle}>
        <span style={{ fontWeight: 500 }}>New &amp; reassigned actions</span>
        <select
          className="form-input"
          aria-label="New & reassigned actions"
          value={taskAssigned}
          disabled={!emailEnabled}
          onChange={(event: ChangeEvent<HTMLSelectElement>) =>
            setTaskAssigned(event.target.value as AssignedPreference)
          }
          style={selectStyle}
        >
          <option value="normal">Normal</option>
          <option value="off">Off</option>
        </select>
      </label>

      <label style={rowStyle}>
        <span style={{ fontWeight: 500 }}>
          Action follow-ups (overdue &amp; due-date reminders)
        </span>
        <select
          className="form-input"
          aria-label="Action follow-ups (overdue & due-date reminders)"
          value={taskFollowups}
          disabled={!emailEnabled}
          onChange={(event: ChangeEvent<HTMLSelectElement>) =>
            setTaskFollowups(event.target.value as FollowupPreference)
          }
          style={selectStyle}
        >
          <option value="normal">Normal</option>
          <option value="weekly">Weekly digest pace</option>
          <option value="off">Off</option>
        </select>
        <span style={helperStyle}>
          Normal uses escalating backoff (day 1, 3, 7, 14, then weekly). Weekly
          caps follow-ups at one email per week per action.
        </span>
      </label>

      <label style={rowStyle}>
        <span style={{ fontWeight: 500 }}>Inspection completed</span>
        <select
          className="form-input"
          aria-label="Inspection completed"
          value={inspectionComplete}
          disabled={!emailEnabled}
          onChange={(event: ChangeEvent<HTMLSelectElement>) =>
            setInspectionComplete(event.target.value as AssignedPreference)
          }
          style={selectStyle}
        >
          <option value="normal">Normal</option>
          <option value="off">Off</option>
        </select>
      </label>

      {[
        ['Action activity (comments, status changes & high-risk findings)', taskActivity, setTaskActivity],
        ['Lab alerts (positive results & incidents)', labAlerts, setLabAlerts],
        ['Risk plan reviews', wrmp, setWrmp],
      ].map(([label, value, setter]) => (
        <label style={rowStyle} key={label as string}>
          <span style={{ fontWeight: 500 }}>{label as string}</span>
          <select
            className="form-input"
            aria-label={label as string}
            value={value as AssignedPreference}
            disabled={!emailEnabled}
            onChange={(event: ChangeEvent<HTMLSelectElement>) =>
              (setter as React.Dispatch<React.SetStateAction<AssignedPreference>>)(
                event.target.value as AssignedPreference,
              )
            }
            style={selectStyle}
          >
            <option value="normal">Normal</option>
            <option value="off">Off</option>
          </select>
        </label>
      ))}

      <button
        type="button"
        className="btn btn-primary"
        disabled={!dirty || saving}
        onClick={handleSave}
        style={{ justifySelf: 'start' }}
      >
        {saving ? 'Saving…' : 'Save preferences'}
      </button>
    </section>
  );
};

export default NotificationPrefsCard;
